import React, { useState, useEffect } from "react";
import "./TableComponent.scss";
import {
  Menu,
  MenuItem,
  IconButton,
  Select,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { ReactComponent as SortIcon } from "../../assets/icons/sort-icon.svg";
import { ReactComponent as LeftArrow } from "../../assets/icons/left-arrow.svg";
import { ReactComponent as RightArrow } from "../../assets/icons/right-arrow.svg";

export const highlightText = (text: string, query: string): JSX.Element[] => {
  if (!query || !text) return [<span key="text">{text || "N/A"}</span>];

  const regex = new RegExp(`(${query})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, index) => {
    if (part.toLowerCase() === query.toLowerCase()) {
      return (
        <span key={`highlight-${index}`} className="highlight">
          {part}
        </span>
      );
    }
    return <span key={`text-${index}`}>{part}</span>;
  });
};

// Add a default cell renderer for N/A values
const DefaultCellRenderer = ({
  value,
  uppercase,
  highlightSearchMatches,
  searchQuery,
}: {
  value: any;
  uppercase?: boolean;
  highlightSearchMatches?: boolean;
  searchQuery?: string;
}) => {
  // Handle null, undefined, empty string, or "N/A" values
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "N/A"
  ) {
    return <span className="na-value">N/A</span>;
  }

  // Convert to string and trim to handle whitespace-only values
  const stringValue = String(value).trim();
  if (stringValue === "") {
    return <span className="na-value">N/A</span>;
  }

  const displayValue = uppercase ? stringValue.toUpperCase() : stringValue;

  if (highlightSearchMatches && searchQuery) {
    return <span>{highlightText(displayValue, searchQuery)}</span>;
  }

  return <span>{displayValue}</span>;
};

export interface TableColumn {
  header: string;
  accessor: string; // Key to access data from the row object
  sortable?: boolean; // Optional flag for sortable columns
  centerAlign?: boolean; // Optional flag for center-aligned columns
  uppercase?: boolean; // Optional flag to display value in uppercase
  cell?: (value: any, row: Record<string, any>) => React.ReactNode; // Optional custom cell renderer
  sortFunction?: (a: Record<string, any>, b: Record<string, any>) => number; // Optional custom sort function
  width?: string; // Optional width for the column
  showTooltip?: boolean; // Optional flag to show tooltip for the column
}

interface TableAction {
  label: string; // Label for the action
  callback: (row: Record<string, any>) => void; // Callback function for the action
  isEnabled?: (row: Record<string, any>) => boolean; // Optional function to determine if the action is enabled
}

interface TableProps {
  columns: TableColumn[];
  data: Record<string, any>[];
  onRowClick?: (row: Record<string, any>) => void; // Optional row click handler
  className?: string; // Optional custom class for styling
  showCheckboxes?: boolean; // Optional flag to show/hide checkboxes
  showSerialNumbers?: boolean; // Optional flag to show/hide serial numbers
  onSelectionChange?: (selectedRows: any[]) => void; // Callback for selected rows
  uniqueAccessor?: string; // Optional unique key for rows
  actions?: TableAction[]; // Optional list of actions
  searchQuery?: string; // Optional search query
  highlightSearchMatches?: boolean; // Optional flag to highlight search matches
  showPagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[]; // Optional array of page size options
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (event: SelectChangeEvent<number>) => void;
  enableRowHover?: boolean; // Optional flag to enable/disable row hover effect
  enableClientSideFiltering?: boolean; // Optional flag to enable/disable client-side filtering
  totalElements?: number; // Total number of elements for pagination
  isServerSidePagination?: boolean; // New prop to indicate server-side pagination
  defaultSort?: { column: string; direction: "asc" | "desc" }; // Optional default sort configuration
}

const TableComponent: React.FC<TableProps> = ({
  columns,
  data,
  onRowClick,
  className,
  showCheckboxes = true,
  showSerialNumbers = true,
  onSelectionChange,
  uniqueAccessor,
  actions = [], // Default to an empty array
  searchQuery = "", // Default to an empty string
  highlightSearchMatches = true, // Default to true
  showPagination = true,
  pageSize = 10,
  pageSizeOptions = [5, 8, 10, 20, 50, 100], // Default page size options
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onPageSizeChange,
  enableRowHover = true, // Default to true
  enableClientSideFiltering = true, // Default to true
  totalElements = 0, // Default to 0
  isServerSidePagination = false, // Default to client-side pagination
  defaultSort, // Optional default sort configuration
}) => {
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(
    defaultSort
      ? { key: defaultSort.column, direction: defaultSort.direction }
      : null
  );
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<Record<string, any> | null>(null);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRows);
    }
  }, [selectedRows, onSelectionChange]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(
        data.map((row) => (uniqueAccessor ? row[uniqueAccessor] : row))
      );
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (row: Record<string, any>) => {
    const rowKey = uniqueAccessor ? row[uniqueAccessor] : row;
    setSelectedRows((prev) =>
      prev.includes(rowKey)
        ? prev.filter((key) => key !== rowKey)
        : [...prev, rowKey]
    );
  };

  const handleSort = (accessor: string) => {
    if (sortConfig?.key === accessor) {
      setSortConfig({
        key: accessor,
        direction: sortConfig.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSortConfig({ key: accessor, direction: "asc" });
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    row: Record<string, any>
  ) => {
    event.stopPropagation();
    setMenuOpen(true);
    setMenuAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    // Use a longer timeout to ensure the menu is fully closed before clearing state
    setTimeout(() => {
      setMenuAnchorEl(null);
      setMenuRow(null);
    }, 300);
  };

  const handleMenuAction = (action: TableAction) => {
    if (menuRow) {
      action.callback(menuRow);
    }
    handleMenuClose();
  };

  const filteredData = React.useMemo(() => {
    if (!enableClientSideFiltering || !searchQuery) return data;
    const query = searchQuery.toLowerCase().trim();
    return data.filter((row) =>
      columns.some((column) => {
        const value = String(row[column.accessor] || "").toLowerCase();
        return value.includes(query);
      })
    );
  }, [data, searchQuery, columns, enableClientSideFiltering]);

  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const column = columns.find((col) => col.accessor === sortConfig.key);
      if (!column) return 0;

      // Use custom sort function if provided
      if (column.sortFunction) {
        return sortConfig.direction === "asc"
          ? column.sortFunction(a, b)
          : column.sortFunction(b, a);
      }

      // Default sorting logic
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === bValue) return 0;
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      const comparison = String(aValue).localeCompare(String(bValue));
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [data, sortConfig, columns]);

  // Apply filtering after sorting
  const filteredAndSortedData = React.useMemo(() => {
    if (!enableClientSideFiltering || !searchQuery) return sortedData;
    const query = searchQuery.toLowerCase().trim();
    return sortedData.filter((row) =>
      columns.some((column) => {
        const value = String(row[column.accessor] || "").toLowerCase();
        return value.includes(query);
      })
    );
  }, [sortedData, searchQuery, columns, enableClientSideFiltering]);

  // Helper function to determine if a column should show tooltip
  const shouldShowTooltip = (accessor: string) => {
    const column = columns.find((col) => col.accessor === accessor);
    return column?.showTooltip || false;
  };

  // Calculate pagination data
  const paginatedData = React.useMemo(() => {
    if (!showPagination) {
      // When pagination is disabled, return all data
      return filteredAndSortedData;
    }
    if (isServerSidePagination) {
      // For server-side pagination, use the data as is since it's already paginated
      return data;
    } else {
      // For client-side pagination, slice the data
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      return filteredAndSortedData.slice(startIndex, endIndex);
    }
  }, [
    data,
    filteredAndSortedData,
    currentPage,
    pageSize,
    isServerSidePagination,
    showPagination,
  ]);

  // Reset to first page when search query changes (only for client-side pagination)
  React.useEffect(() => {
    if (!isServerSidePagination && onPageChange) {
      onPageChange(1);
    }
  }, [searchQuery, onPageChange, isServerSidePagination]);

  const renderPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];
    const lastPage = isServerSidePagination ? totalPages - 1 : totalPages;

    if (totalPages <= 5) {
      for (let i = 1; i <= lastPage; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pageNumbers.push(1, 2, 3, 4, "...", lastPage);
      } else if (currentPage >= lastPage - 2) {
        pageNumbers.push(
          1,
          "...",
          lastPage - 3,
          lastPage - 2,
          lastPage - 1,
          lastPage
        );
      } else {
        pageNumbers.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          lastPage
        );
      }
    }

    return (
      <>
        {pageNumbers.map((page, index) => (
          <button
            key={index}
            onClick={() =>
              typeof page === "number" && onPageChange && onPageChange(page)
            }
            className={`min-h-[40px] min-w-[40px] px-2 text-[14px] font-[600] ${
              index === pageNumbers.length - 1 ? "" : "border-r"
            } border-[#CBD2D9] ${
              currentPage === page
                ? "bg-[#F5F7F7] text-[#37372D]"
                : "bg-white hover:bg-[#F5F7F7] text-[#838285]"
            }`}
          >
            {page}
          </button>
        ))}
      </>
    );
  };

  // Calculate the range of items being displayed
  const getDisplayRange = () => {
    if (isServerSidePagination) {
      const start = (currentPage - 1) * pageSize + 1;
      const end = Math.min(currentPage * pageSize, totalElements);
      return { start, end };
    } else {
      const start = (currentPage - 1) * pageSize + 1;
      const end = Math.min(
        currentPage * pageSize,
        filteredAndSortedData.length
      );
      return { start, end };
    }
  };

  const { start, end } = getDisplayRange();
  const totalItems = isServerSidePagination
    ? totalElements
    : filteredAndSortedData.length;

  return (
    <div className={`table-component-wrapper`}>
      <div className={`table-component ${className || ""}`}>
        <table>
          <thead>
            <tr>
              {showCheckboxes && (
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={
                      selectedRows.length === data.length && data.length > 0
                    }
                  />
                </th>
              )}
              {showSerialNumbers && (
                <th className="text-center w-[80px]">Sl. No.</th>
              )}
              {columns.map((column) => (
                <th
                  key={column.accessor}
                  onClick={() => column.sortable && handleSort(column.accessor)}
                  className={`${column.sortable ? "sortable-header" : ""} ${
                    column.centerAlign ? "text-center" : ""
                  }`}
                  style={{ width: column.width }}
                >
                  <div
                    className={`header ${
                      column.sortable ? "sortable" : ""
                    } flex items-center gap-1 ${
                      column.centerAlign ? "justify-center" : ""
                    }`}
                  >
                    {column.header}
                    {column.sortable && (
                      <SortIcon
                        className={`sort_icon ${
                          sortConfig?.key === column.accessor ? "sorted" : ""
                        } ${
                          sortConfig?.key === column.accessor &&
                          sortConfig.direction === "desc"
                            ? "rotate-180"
                            : ""
                        }`}
                      />
                    )}
                  </div>
                </th>
              ))}
              {actions.length > 0 && <th className="text-center w-[60px]"></th>}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={uniqueAccessor ? row[uniqueAccessor] : rowIndex}
                onClick={() => onRowClick && onRowClick(row)}
                className={`${onRowClick ? "clickable-row" : ""} ${
                  enableRowHover ? "hoverable-row" : ""
                } ${
                  selectedRows.includes(
                    uniqueAccessor ? row[uniqueAccessor] : row
                  )
                    ? "selected-row"
                    : ""
                } ${
                  menuRow &&
                  (uniqueAccessor
                    ? menuRow[uniqueAccessor] === row[uniqueAccessor]
                    : menuRow === row)
                    ? "highlighted-row"
                    : ""
                }`}
              >
                {showCheckboxes && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(
                        uniqueAccessor ? row[uniqueAccessor] : row
                      )}
                      onChange={() => handleRowSelect(row)}
                    />
                  </td>
                )}
                {showSerialNumbers && (
                  <td className="text-center">
                    {(currentPage - 1) * pageSize + rowIndex + 1}
                  </td>
                )}
                {columns.map((column, colIndex) => (
                  <td
                    key={`${rowIndex}-${column.accessor}`}
                    className={column.centerAlign ? "text-center" : ""}
                    style={{ width: column.width }}
                    title={
                      shouldShowTooltip(column.accessor)
                        ? row[column.accessor]
                          ? String(row[column.accessor]).trim()
                          : "N/A"
                        : undefined
                    }
                  >
                    {column.cell ? (
                      highlightSearchMatches && searchQuery ? (
                        <span>
                          {React.cloneElement(
                            column.cell(
                              row[column.accessor],
                              row
                            ) as React.ReactElement,
                            {
                              highlightSearchMatches,
                              searchQuery,
                            }
                          )}
                        </span>
                      ) : (
                        column.cell(row[column.accessor], row)
                      )
                    ) : (
                      <DefaultCellRenderer
                        value={row[column.accessor]}
                        uppercase={column.uppercase}
                        highlightSearchMatches={highlightSearchMatches}
                        searchQuery={searchQuery}
                      />
                    )}
                  </td>
                ))}
                {actions.length > 0 && (
                  <td className="text-center">
                    <IconButton
                      onClick={(event) => handleMenuOpen(event, row)}
                      size="small"
                      disabled={actions.every(
                        (action) =>
                          action.isEnabled &&
                          !action.isEnabled(sortedData[rowIndex])
                      )}
                    >
                      <MoreHorizIcon />
                    </IconButton>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          MenuListProps={{
            onClick: (e) => e.stopPropagation(),
          }}
          TransitionProps={{
            onExited: () => {
              // This ensures the menu is fully closed before any state changes
              setMenuAnchorEl(null);
              setMenuRow(null);
            },
          }}
        >
          {actions.map((action, index) => (
            <MenuItem
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                handleMenuAction(action);
              }}
              disabled={
                !menuOpen || (action.isEnabled && !action.isEnabled(menuRow!))
              }
            >
              {action.label}
            </MenuItem>
          ))}
        </Menu>
      </div>
      {showPagination && totalPages > 1 && (
        <div className="user-select-none flex flex-col items-center mt-4 space-y-4">
          <div className="flex justify-end items-center w-full">
            <div className="text-[14px] font-[500] text-[#838285] mr-4">
              Showing {start} to {end} of {totalItems}
            </div>
            <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
              <button
                onClick={() => onPageChange && onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`flex items-center justify-center min-h-[40px] min-w-[40px] border-r border-[#CBD2D9] ${
                  currentPage === 1
                    ? "bg-white text-[#838285] cursor-not-allowed"
                    : "bg-white hover:bg-[#F5F7F7] text-[#838285]"
                }`}
              >
                <LeftArrow />
              </button>
              {renderPageNumbers()}
              <button
                onClick={() => onPageChange && onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`flex items-center justify-center min-h-[40px] min-w-[40px] border-l border-[#CBD2D9] ${
                  currentPage === totalPages
                    ? "bg-white text-[#838285] cursor-not-allowed"
                    : "bg-white hover:bg-[#F5F7F7] text-[#838285]"
                }`}
              >
                <RightArrow />
              </button>
            </div>
            <div className="text-[14px] font-[500] text-[#838285] mx-4">
              Per page
            </div>
            <FormControl size="small">
              <Select
                value={pageSize}
                onChange={onPageSizeChange}
                className="min-w-[80px] bg-white hover:bg-[#F5F7F7] text-[#838285]"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #CBD2D9",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #CBD2D9",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid #CBD2D9",
                  },
                }}
              >
                {pageSizeOptions.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableComponent;
